import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TrackSelect } from "@/components/TrackSelect";
import { fieldInputCls } from "@/components/Field";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const distM = (a, b) => Math.hypot((a.lat - b.lat) * 111320, (a.lon - b.lon) * 111320 * Math.cos((a.lat * Math.PI) / 180));

export const TrackFinder = ({ tracks, trackId, csvTrack, onSelect, onClose }) => {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.get("/geocode", { params: { q } });
      setResults(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const choose = async (r) => {
    const near = tracks.find((tr) => tr.lat != null && distM(tr, r) < 1500);
    if (near) {
      onSelect(near.id);
      return;
    }
    const aliases = !trackId && csvTrack ? [csvTrack] : [];
    const { data } = await api.post("/tracks", { name: r.name, city: r.city, country: r.country, lat: r.lat, lon: r.lon, aliases, notes: "" });
    toast.success(t("track_created"));
    onSelect(data.id);
  };

  return (
    <div data-testid="track-finder" className="space-y-2 bg-[#090A0F] border border-[#282D3D] rounded-sm p-3 mb-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase text-slate-400 shrink-0">{t("from_database")}</span>
        <div className="min-w-0 flex-1"><TrackSelect tracks={tracks} value={trackId} onChange={(id) => id && onSelect(id)} testid="telemetry-track-select" className="h-8 text-xs w-full [&>span]:truncate" /></div>
        {onClose && <button type="button" data-testid="track-finder-close" onClick={onClose} className="p-1 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
      </div>
      <div className="flex gap-2">
        <Input data-testid="track-finder-input" placeholder={t("search_track_or_address")} value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} className={`${fieldInputCls} h-8 text-xs`} />
        <button type="button" data-testid="track-finder-search-btn" onClick={search} disabled={busy} className="px-2.5 border border-[#282D3D] rounded-sm text-slate-300 hover:text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>
      {results && results.length === 0 && <p data-testid="track-finder-empty" className="font-mono text-[10px] text-[#FFB800]">{t("no_results")}</p>}
      {results?.map((r, i) => (
        <button type="button" key={`${r.lat}-${r.lon}`} data-testid={`track-finder-result-${i}`} onClick={() => choose(r)}
          className="w-full flex items-start gap-2 text-left p-2 border border-[#282D3D] rounded-sm hover:border-[#FFB800] transition-colors duration-200">
          <MapPin className="w-3.5 h-3.5 text-[#FF3B30] mt-0.5 shrink-0" />
          <span className="min-w-0"><span className="block text-sm font-semibold truncate">{r.name}</span><span className="block font-mono text-[10px] text-slate-500 truncate">{r.label}</span></span>
        </button>
      ))}
    </div>
  );
};
